/**
 * Wrapper around React.lazy that retries failed dynamic imports.
 *
 * After a deployment, old JS chunk filenames are replaced with new hashed ones.
 * If a user's browser has a cached version of the app, lazy-loaded modules will
 * 404 because the old chunk no longer exists. This utility:
 *
 * 1. Retries the import up to 3 times with exponential backoff
 * 2. If all retries fail, forces a full page reload (once) to fetch fresh assets
 * 3. Uses sessionStorage to prevent infinite reload loops
 */

import { lazy, type ComponentType } from "react"

const RELOAD_KEY = "dynamic-import-reload"

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function lazyWithRetry<T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
  retries = 3,
  interval = 1000,
): React.LazyExoticComponent<T> {
  return lazy(async () => {
    for (let attempt = 0; attempt < retries; attempt++) {
      try {
        return await importFn()
      } catch (error) {
        if (attempt < retries - 1) {
          // Wait with exponential backoff before retrying
          await new Promise((resolve) =>
            setTimeout(resolve, interval * 2 ** attempt),
          )
        }
      }
    }

    // All retries exhausted — reload page once to get fresh assets
    const hasReloaded = sessionStorage.getItem(RELOAD_KEY)
    if (!hasReloaded) {
      sessionStorage.setItem(RELOAD_KEY, "true")
      window.location.reload()
      // Return a never-resolving promise to prevent rendering stale content
      return new Promise(() => {})
    }

    // Already reloaded once and still failing — clear flag and throw
    sessionStorage.removeItem(RELOAD_KEY)
    throw new Error("Failed to load module after retries and page reload")
  })
}

/**
 * Clear the reload flag on successful app startup.
 * Call this once in your app entry point (e.g., main.tsx).
 */
export function clearDynamicImportReloadFlag(): void {
  sessionStorage.removeItem(RELOAD_KEY)
}

export default lazyWithRetry
